import React, { useState } from 'react';

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const OppositionView: React.FC = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [channel, setChannel] = useState<'web' | 'email' | 'phone' | 'letter' | 'unsubscribe_link'>('web');
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [reference, setReference] = useState('');
  const [proofToken, setProofToken] = useState('');

  // Récupérer le token depuis un lien "me désinscrire" (?token=...)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      setProofToken(t);
      setChannel('unsubscribe_link');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed) {
      setStatus('error');
      setMessage('Vous devez confirmer être la personne concernée.');
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}?action=submit_opposition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason: reason || null, channel, proof_token: proofToken || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus('error');
        setMessage(data.error || `Erreur HTTP ${res.status}`);
        return;
      }
      setStatus('success');
      setReference(data.reference || '');
      setMessage(data.message || 'Votre demande a été enregistrée.');
    } catch {
      setStatus('error');
      setMessage('Impossible de contacter le serveur. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-8 py-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-black uppercase tracking-widest bg-white/20 px-2.5 py-1 rounded-lg">
              RGPD Art. 21
            </span>
          </div>
          <h1 className="text-2xl font-black">Opposition au traitement de vos données</h1>
          <p className="text-sm text-indigo-100 mt-1 leading-relaxed">
            Conformément à l'article 21 du RGPD, vous pouvez à tout moment vous opposer au traitement
            de vos données personnelles à des fins de prospection commerciale.
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-black text-slate-900 mb-2">Demande enregistrée</h2>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">{message}</p>
              {reference && (
                <div className="inline-block bg-slate-100 border border-slate-200 rounded-xl px-4 py-2 text-xs font-mono text-slate-700">
                  Référence : <strong>{reference}</strong>
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-6">
                Conservez cette référence pour tout échange ultérieur.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                  Votre adresse email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Nous utiliserons cette adresse pour identifier vos données et vous répondre.
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                  Motif (facultatif)
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Vous n'êtes pas obligé de motiver votre opposition à la prospection commerciale."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                  Canal par lequel vous avez été contacté
                </label>
                <select
                  value={channel}
                  onChange={e => setChannel(e.target.value as typeof channel)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="web">Site web / formulaire</option>
                  <option value="email">Email</option>
                  <option value="phone">Téléphone</option>
                  <option value="letter">Courrier postal</option>
                  <option value="unsubscribe_link">Lien de désinscription</option>
                </select>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs text-slate-600 leading-relaxed">
                  Je confirme être la personne concernée par les données visées par cette demande
                  et comprendre qu'une usurpation d'identité est passible de sanctions pénales.
                </span>
              </label>

              {status === 'error' && message && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-xs text-rose-700">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-black uppercase tracking-wider text-xs py-4 rounded-xl hover:shadow-xl hover:shadow-indigo-500/30 transition-all disabled:opacity-60"
              >
                {status === 'submitting' ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>

              <p className="text-[10px] text-slate-400 leading-relaxed text-center pt-2">
                Vous recevrez un accusé de réception sous 48h et une réponse définitive sous 30 jours.
                En cas de désaccord, vous pouvez saisir la <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-bold">CNIL</a>.
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 px-8 py-4 flex items-center justify-between text-[10px] text-slate-400">
          <span>ORION CRM — Conforme RGPD</span>
          <a href="/politique-de-confidentialite" className="hover:text-slate-600 font-bold">
            Politique de confidentialité
          </a>
        </div>
      </div>
    </div>
  );
};

export default OppositionView;
